import { useState } from "react";
import { Rss, Mail, Check } from "lucide-react";
import { toast } from "sonner";

const alleThemas = ["Beleid", "Arbeidsmarkt", "Cijfers", "Methodiek"];

const API = process.env.REACT_APP_BACKEND_URL;

export default function KennisbankSubscribe() {
  const [email, setEmail] = useState("");
  const [themas, setThemas] = useState([]);
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleThema = (t) =>
    setThemas((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/kennisbank/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, themas, website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Er ging iets mis. Probeer het opnieuw.");
      } else {
        setDone(true);
        toast.success("Je bent aangemeld. Check je e-mail voor de bevestiging.");
      }
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="kennisbank-subscribe"
      className="rounded-3xl bg-[#1D2A22] text-[#FAF7F1] p-8 sm:p-10 relative overflow-hidden"
    >
      {/* decorative rss glyph */}
      <div className="absolute -right-4 -top-4 opacity-[0.08] pointer-events-none">
        <Rss size={180} />
      </div>
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D19B72] mb-3">
          Kennisbank-abonnement
        </p>
        <h3 className="font-heading text-3xl sm:text-4xl leading-tight font-light">
          Ontvang elk nieuw artikel{" "}
          <span className="italic text-[#D19B72]">in je inbox</span>.
        </h3>
        <p className="mt-4 text-[#FAF7F1]/70 max-w-xl">
          Kies je thema's. We sturen alleen bij een nieuw artikel, geen
          nieuwsbrief, geen ruis. Uitschrijven kan altijd.
        </p>

        {done ? (
          <div className="mt-8 rounded-2xl bg-[#FAF7F1]/10 border border-[#FAF7F1]/20 p-6 flex items-start gap-3">
            <Check size={20} className="text-[#D19B72] shrink-0 mt-0.5" />
            <div>
              <p className="font-heading text-xl text-[#FAF7F1]">Je bent aangemeld.</p>
              <p className="mt-1 text-sm text-[#FAF7F1]/70">
                Check je e-mail voor de bevestiging.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {/* honeypot */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="absolute -left-[9999px] w-1 h-1"
              aria-hidden="true"
            />
            <div className="flex flex-wrap gap-2">
              {alleThemas.map((t) => {
                const on = themas.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleThema(t)}
                    data-testid={`kb-thema-${t}`}
                    className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
                      on
                        ? "bg-[#D19B72] text-[#1D2A22] border-[#D19B72]"
                        : "border-[#FAF7F1]/25 text-[#FAF7F1]/85 hover:border-[#D19B72]"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Mail
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FAF7F1]/50"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voornaam@gemeente.nl"
                  data-testid="kb-subscribe-email"
                  className="w-full bg-[#FAF7F1]/10 border border-[#FAF7F1]/20 rounded-full pl-11 pr-5 py-3 text-[#FAF7F1] placeholder:text-[#FAF7F1]/40 outline-none focus:border-[#D19B72]"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="kb-subscribe-submit"
                className="rounded-full bg-[#D19B72] hover:bg-[#FAF7F1] text-[#1D2A22] px-6 py-3 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {busy ? "Aanmelden…" : "Abonneren"}
              </button>
            </div>
            <p className="text-xs text-[#FAF7F1]/50">
              We versturen alleen bij een nieuw artikel. Uitschrijven kan altijd.
              Ook via{" "}
              <a
                href={`${API}/api/kennisbank/feed.xml`}
                target="_blank"
                rel="noreferrer"
                className="text-[#D19B72] link-underline"
                data-testid="kb-rss-link"
              >
                RSS
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
